import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../App';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, cartTotal, cartCount } = useCart();

  if (cart.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-cs-card border border-cs-border flex items-center justify-center">
            <ShoppingBag className="w-10 h-10 text-cs-muted" />
          </div>
          <h1 className="font-anton text-4xl uppercase text-cs-text mb-4">Your Cart is Empty</h1>
          <p className="font-mono text-sm text-cs-muted mb-6">Time to add some gear to your wardrobe.</p>
          <Link to="/shop" className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-8 py-4 hover:bg-cs-olive2 transition-colors inline-flex items-center gap-2" data-testid="shop-link">
            Shop Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="cart-page">
      <section className="bg-cs-dark border-b border-cs-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-2">Your Gear</p>
          <h1 className="font-anton text-4xl md:text-5xl uppercase text-cs-text tracking-wider" data-testid="cart-title">
            Cart ({cartCount})
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.map((item, index) => (
              <div key={`${item.product_id}-${item.size}-${item.color}`} className="bg-cs-card border border-cs-border flex flex-col sm:flex-row gap-4 p-4" data-testid={`cart-item-${index}`}>
                <Link to={`/product/${item.product_id}`} className="w-full sm:w-28 h-28 flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover border border-cs-border" />
                </Link>
                <div className="flex-1 flex flex-col">
                  <div className="flex-1">
                    <Link to={`/product/${item.product_id}`} className="font-bold text-cs-text text-sm uppercase tracking-wide hover:text-cs-ochre transition-colors">
                      {item.name}
                    </Link>
                    <p className="font-mono text-xs text-cs-muted mt-1">{item.size} / {item.color}</p>
                    <p className="font-mono text-cs-ochre text-lg mt-1">${item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-cs-border">
                      <button onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity - 1)} className="p-2 text-cs-muted hover:text-cs-text hover:bg-cs-bg transition-colors" data-testid={`decrease-${index}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center font-mono text-sm text-cs-text">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity + 1)} className="p-2 text-cs-muted hover:text-cs-text hover:bg-cs-bg transition-colors" data-testid={`increase-${index}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(item.product_id, item.size, item.color)} className="p-2 text-cs-alert/60 hover:text-cs-alert transition-colors" data-testid={`remove-${index}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="sm:text-right">
                  <p className="font-mono text-xs text-cs-muted">Subtotal</p>
                  <p className="font-mono text-lg text-cs-ochre font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-cs-card border border-cs-border p-6 sticky top-24" data-testid="order-summary">
              <h2 className="font-anton text-xl uppercase text-cs-text mb-6 tracking-wide">Order Summary</h2>
              <div className="space-y-3 font-mono text-sm">
                <div className="flex justify-between"><span className="text-cs-muted">Subtotal ({cartCount} items)</span><span className="text-cs-text">${cartTotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-cs-muted">Shipping</span><span className="text-cs-text">{cartTotal >= 75 ? 'FREE' : '$5.99'}</span></div>
                <div className="flex justify-between"><span className="text-cs-muted">Est. Tax</span><span className="text-cs-text">${(cartTotal * 0.08).toFixed(2)}</span></div>
              </div>
              <div className="border-t border-cs-border mt-6 pt-6">
                <div className="flex justify-between items-center mb-6">
                  <span className="font-anton text-xl uppercase text-cs-text tracking-wide">Total</span>
                  <span className="font-mono text-2xl text-cs-ochre font-bold">${(cartTotal + (cartTotal >= 75 ? 0 : 5.99) + (cartTotal * 0.08)).toFixed(2)}</span>
                </div>
                <Link to="/checkout" className="block w-full bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm text-center py-4 hover:bg-cs-olive2 transition-colors" data-testid="checkout-btn">
                  Proceed to Checkout
                </Link>
                <Link to="/shop" className="block w-full border border-cs-border text-cs-muted font-mono uppercase tracking-wider text-sm text-center py-3 mt-3 hover:border-cs-ochre hover:text-cs-ochre transition-colors" data-testid="continue-shopping">
                  Continue Shopping
                </Link>
              </div>
              {cartTotal < 75 && (
                <div className="mt-6 bg-cs-olive/20 border border-cs-olive/40 p-4">
                  <p className="font-mono text-xs text-cs-text">Add <span className="text-cs-ochre font-bold">${(75 - cartTotal).toFixed(2)}</span> more for FREE shipping!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
